/**
 * @fileoverview Componente de gerenciamento de usuários.
 * Permite inclusão, alteração, exclusão e visualização de usuários.
 * Interface responsiva com grid adaptável para desktop e mobile.
 * Integra com endpoints REST para CRUD de usuários e roles.
 * 
 * @module UserManager
 * @requires react
 * @requires @mui/material
 * @requires ../../services/api
 * @since 1.0.0
 * @version 2.1.0 - Corrigido payload para enviar 'nome' e 'empty' como senha padrão
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Paper,
  IconButton,
  Box,
  useMediaQuery,
  useTheme,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Snackbar,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import api from '../../services/api';

/**
 * Valida se o email possui formato válido.
 * @param {string} email - Email a ser validado
 * @returns {boolean} True se válido
 */
const validateEmail = (email) => {
  const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return regex.test(email);
};

/**
 * Componente principal de gerenciamento de usuários.
 * @component
 * @param {Object} props
 * @param {boolean} props.open - Estado de abertura do modal
 * @param {Function} props.onClose - Callback para fechar modal
 * @returns {JSX.Element}
 */
export default function UserManager({ open, onClose }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [formOpen, setFormOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [formMode, setFormMode] = useState('create');
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    roles: [],
    avatar: '',
  });
  const [errors, setErrors] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [roleFilter, setRoleFilter] = useState('todas');
  const [availableRoles, setAvailableRoles] = useState([]);
  const [orderBy, setOrderBy] = useState('nome');
  const [order, setOrder] = useState('asc');

  /**
   * Carrega lista de usuários do backend com filtros.
   * Aplica filtros de busca por nome/email e role.
   * Trata múltiplos formatos de resposta da API.
   * @async
   * @function loadUsers
   * @returns {Promise<void>}
   */
  const loadUsers = async () => {
    try {
      setLoading(true);
      const params = {};
      if (searchTerm) params.search = searchTerm;
      if (roleFilter && roleFilter !== 'todas') params.role = roleFilter;
      
      const response = await api.get('/users', { params });
      const usersData = response.data?.data?.users || response.data?.users || response.data || [];
      setUsers(Array.isArray(usersData) ? usersData : []);
    } catch (error) {
      showSnackbar('Erro ao carregar usuários', 'error');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Carrega roles disponíveis do endpoint /api/v1/roles.
   * Usa fallback com roles padrão em caso de erro.
   * @async
   * @function loadRoles
   * @returns {Promise<void>}
   */
  const loadRoles = async () => {
    try {
      const response = await api.get('/roles');
      const rolesData = response.data?.data?.roles || response.data?.roles || [];
      setAvailableRoles(rolesData);
    } catch (error) {
      setAvailableRoles([{ id: 1, name: 'Admin' }, { id: 2, name: 'User' }, { id: 3, name: 'Manager' }]);
    }
  };

  /**
   * Executa busca ao clicar no ícone de lupa.
   * Atualiza searchTerm que dispara useEffect para recarregar usuários.
   * @function handleSearch
   * @returns {void}
   */
  const handleSearch = useCallback(() => {
    setSearchTerm(searchInput);
  }, [searchInput]);

  /**
   * Limpa o campo de busca e remove o filtro aplicado.
   * @function handleClearSearch
   * @returns {void}
   */
  const handleClearSearch = () => {
    setSearchInput('');
    setSearchTerm('');
  };

  useEffect(() => {
    if (open) {
      loadUsers();
      loadRoles();
    }
  }, [open, searchTerm, roleFilter]);

  /**
   * Manipula ordenação de colunas.
   * @function handleSort
   * @param {string} property - Nome da propriedade para ordenar
   * @returns {void}
   */
  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  /**
   * Usuários ordenados baseado na coluna e direção selecionadas.
   * @constant {Array} sortedUsers
   */
  const sortedUsers = useMemo(() => {
    return [...users].sort((a, b) => {
      let aValue = a[orderBy] || '';
      let bValue = b[orderBy] || '';
      
      if (orderBy === 'nome') {
        aValue = (a.name || a.nome || '').toLowerCase();
        bValue = (b.name || b.nome || '').toLowerCase();
      } else if (orderBy === 'email') {
        aValue = (a.email || '').toLowerCase();
        bValue = (b.email || '').toLowerCase();
      } else if (orderBy === 'roles') {
        aValue = (a.roles?.map(r => r.name || r.alias).join(', ') || '').toLowerCase();
        bValue = (b.roles?.map(r => r.name || r.alias).join(', ') || '').toLowerCase();
      }
      
      if (order === 'asc') {
        return aValue.localeCompare(bValue);
      }
      return bValue.localeCompare(aValue);
    });
  }, [users, order, orderBy]);

  /**
   * Exibe mensagem de feedback via Snackbar.
   * @function showSnackbar
   * @param {string} message - Mensagem a ser exibida
   * @param {string} [severity='success'] - Tipo da mensagem (success, error, warning, info)
   * @returns {void}
   */
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  /**
   * Abre formulário para criar novo usuário.
   * Limpa formData e erros antes de abrir.
   * @function handleCreate
   * @returns {void}
   */
  const handleCreate = () => {
    setFormMode('create');
    setFormData({ name: '', email: '', password: '', roles: [], avatar: '' });
    setErrors({});
    setFormOpen(true);
  };

  /**
   * Abre formulário para editar usuário.
   * Preenche formData com dados do usuário selecionado.
   * @function handleEdit
   * @param {Object} user - Usuário a ser editado
   * @returns {void}
   */
  const handleEdit = (user) => {
    setFormMode('edit');
    setSelectedUser(user);
    setFormData({
      name: user.name || user.nome,
      email: user.email,
      password: '',
      roles: user.roles?.map(role => role.id) || [],
      avatar: user.avatar || '',
    });
    setErrors({});
    setFormOpen(true);
  };

  /**
   * Abre modal de visualização de detalhes do usuário.
   * @function handleView
   * @param {Object} user - Usuário a ser visualizado
   * @returns {void}
   */
  const handleView = (user) => {
    setSelectedUser(user);
    setViewOpen(true);
  };

  /**
   * Remove usuário após confirmação.
   * Exibe diálogo de confirmação antes de excluir.
   * @async
   * @function handleDelete
   * @param {Object} user - Usuário a ser excluído
   * @returns {Promise<void>}
   */
  const handleDelete = async (user) => {
    if (!window.confirm(`Deseja realmente excluir o usuário ${user.name}?`)) {
      return;
    }

    try {
      await api.delete(`/users/${user.id}`);
      showSnackbar('Usuário excluído com sucesso');
      loadUsers();
    } catch (error) {
      showSnackbar('Erro ao excluir usuário', 'error');
    }
  };

  /**
   * Valida campos do formulário.
   * Verifica nome, email (formato) e roles obrigatórios.
   * Senha opcional na edição, mínimo 6 caracteres se fornecida.
   * @function validateForm
   * @returns {boolean} True se válido, false caso contrário
   */
  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Nome é obrigatório';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email é obrigatório';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Email inválido';
    }

    if (!formData.roles || formData.roles.length === 0) {
      newErrors.roles = 'Selecione pelo menos um role';
    }

    if (formMode === 'edit' && formData.password && formData.password.length < 6) {
      newErrors.password = 'Senha deve ter no mínimo 6 caracteres';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Submete formulário (criar ou editar).
   * Valida antes de enviar. Remove senha vazia na edição.
   * @async
   * @function handleSubmit
   * @returns {Promise<void>}
   */
  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      let roleIds = formData.roles;
      
      // Se nenhum role selecionado na criação, buscar ID da role "user"
      if (formMode === 'create' && (!roleIds || roleIds.length === 0)) {
        const userRole = availableRoles.find(role => 
          role.alias === 'user' || role.name?.toLowerCase() === 'user'
        );
        roleIds = userRole ? [userRole.id] : [];
      }
      
      if (formMode === 'create') {
        const payload = {
          nome: formData.name,
          email: formData.email,
          password: 'empty',
          role_ids: roleIds,
          avatar: formData.avatar
        };
        await api.post('/users', payload);
        showSnackbar('Usuário criado com sucesso');
      } else {
        const payload = {
          nome: formData.name,
          email: formData.email,
          role_ids: roleIds,
          avatar: formData.avatar
        };
        if (formData.password) {
          payload.password = formData.password;
        }
        await api.put(`/users/${selectedUser.id}`, payload);
        showSnackbar('Usuário atualizado com sucesso');
      }
      setFormOpen(false);
      loadUsers();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Erro ao salvar usuário', 'error');
    }
  };

  /**
   * Renderiza grid desktop com todas as colunas.
   * Exibe nome, email, roles e ações (visualizar, editar, excluir).
   * Colunas ordenáveis com TableSortLabel.
   * @function renderDesktopGrid
   * @returns {JSX.Element} Grid desktop
   */
  const renderDesktopGrid = () => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>
              <TableSortLabel
                active={orderBy === 'nome'}
                direction={orderBy === 'nome' ? order : 'asc'}
                onClick={() => handleSort('nome')}
              >
                Nome
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={orderBy === 'email'}
                direction={orderBy === 'email' ? order : 'asc'}
                onClick={() => handleSort('email')}
              >
                Email
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={orderBy === 'roles'}
                direction={orderBy === 'roles' ? order : 'asc'}
                onClick={() => handleSort('roles')}
              >
                Roles
              </TableSortLabel>
            </TableCell>
            <TableCell align="right">Ações</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedUsers
            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            .map((user) => (
              <TableRow key={user.id}>
                <TableCell>{user.name || user.nome}</TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>
                  {user.roles?.map(role => role.name || role.alias).join(', ') || '-'}
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Visualizar">
                    <IconButton size="small" onClick={() => handleView(user)}>
                      <VisibilityIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Editar">
                    <IconButton size="small" onClick={() => handleEdit(user)}>
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Excluir">
                    <IconButton size="small" onClick={() => handleDelete(user)}>
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  /**
   * Renderiza grid mobile com scroll e apenas coluna nome.
   * Ações compactas na segunda coluna.
   * @function renderMobileGrid
   * @returns {JSX.Element} Grid mobile
   */
  const renderMobileGrid = () => (
    <Box sx={{ maxHeight: '60vh', overflow: 'auto' }}>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nome</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((user) => (
                <TableRow key={user.id}>
                  <TableCell>{user.name || user.nome}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleView(user)}>
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleEdit(user)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDelete(user)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth fullScreen={isMobile}>
        <DialogTitle>
          Gerenciamento de Usuários
          <IconButton
            onClick={onClose}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="Buscar por nome ou email..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              InputProps={{
                startAdornment: (
                  <IconButton size="small" onClick={handleSearch} title="Buscar">
                    <SearchIcon />
                  </IconButton>
                ),
                endAdornment: searchInput && (
                  <IconButton size="small" onClick={handleClearSearch} title="Limpar">
                    <CloseIcon />
                  </IconButton>
                )
              }}
              sx={{ flexGrow: 1, minWidth: 200 }}
            />
            <FormControl variant="outlined" size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Filtrar por Role</InputLabel>
              <Select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                label="Filtrar por Role"
              >
                <MenuItem value="todas">Todas</MenuItem>
                {availableRoles.map((role) => (
                  <MenuItem key={role.id || role.name} value={role.alias || role.name}>
                    {role.name || role.alias}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={handleCreate}
            >
              Adicionar
            </Button>
          </Box>

          {isMobile ? renderMobileGrid() : renderDesktopGrid()}

          <TablePagination
            component="div"
            count={users.length}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50]}
            labelRowsPerPage="Linhas por página:"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Formulário */}
      <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {formMode === 'create' ? 'Novo Usuário' : 'Editar Usuário'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Nome"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              error={!!errors.name}
              helperText={errors.name}
              fullWidth
            />
            <TextField
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              error={!!errors.email}
              helperText={errors.email}
              fullWidth
            />
            {formMode === 'edit' && (
              <TextField
                label="Nova Senha (deixe em branco para manter)"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                error={!!errors.password}
                helperText={errors.password}
                fullWidth
              />
            )}
            <FormControl fullWidth error={!!errors.roles}>
              <InputLabel>Roles *</InputLabel>
              <Select
                multiple
                value={formData.roles}
                onChange={(e) => setFormData({ ...formData, roles: e.target.value })}
                label="Roles *"
              >
                {availableRoles.map((role) => (
                  <MenuItem key={role.id || role.name} value={role.id}>
                    {role.name || role.alias}
                  </MenuItem>
                ))}
              </Select>
              {errors.roles && <Box sx={{ color: 'error.main', fontSize: '0.75rem', mt: 0.5 }}>{errors.roles}</Box>}
            </FormControl>
            <TextField
              label="Avatar URL"
              value={formData.avatar}
              onChange={(e) => setFormData({ ...formData, avatar: e.target.value })}
              fullWidth
              helperText="URL da imagem do avatar (opcional)"
            />
            {formData.avatar && (
              <Box sx={{ textAlign: 'center' }}>
                <img
                  src={formData.avatar}
                  alt="Preview"
                  style={{ maxWidth: '100px', borderRadius: '50%' }}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)}>Cancelar</Button>
          <Button onClick={handleSubmit} variant="contained">
            {formMode === 'create' ? 'Criar' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Visualização */}
      <Dialog open={viewOpen} onClose={() => setViewOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Detalhes do Usuário</DialogTitle>
        <DialogContent>
          {selectedUser && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <TextField label="Nome" value={selectedUser.name} disabled fullWidth />
              <TextField label="Email" value={selectedUser.email} disabled fullWidth />
              <TextField
                label="Roles"
                value={selectedUser.roles?.map(role => role.name || role.alias).join(', ') || '-'}
                disabled
                fullWidth
              />
              {selectedUser.avatar && (
                <Box sx={{ textAlign: 'center', mt: 2 }}>
                  <img
                    src={selectedUser.avatar}
                    alt="Avatar"
                    style={{ maxWidth: '150px', borderRadius: '50%' }}
                  />
                </Box>
              )}
              {selectedUser.createdAt && (
                <TextField
                  label="Criado em"
                  value={new Date(selectedUser.createdAt).toLocaleString('pt-BR')}
                  disabled
                  fullWidth
                />
              )}
              {selectedUser.updatedAt && (
                <TextField
                  label="Atualizado em"
                  value={new Date(selectedUser.updatedAt).toLocaleString('pt-BR')}
                  disabled
                  fullWidth
                />
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewOpen(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
